import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  leadGenerationReportApi,
  type CohortWindow,
  type Drilldown,
  type DrilldownKpi,
  type DisputeBucket,
  type DisputeGroupBy,
  type LeadCohorts,
  type LeadDisputes,
  type LeadGenerationFilters,
  type LeadGenerationReport,
  type LeadProviders,
  type LeadProvidersComplete,
} from '../api/leadGenerationReport'
import { leadsApi, type LeadSourceOption } from '../api/leads'
import { downloadExportFile, saveExportFile, withFormat } from '../api/exports'
import { useFeatureFlag } from '../lib/featureFlags'
import { dataSourceBadge, type WithDataSource } from '../api/reportingAggregates'
import {
  CardGrid, StatGrid, Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  DataTable,
  DataTableEmptyRow,
  FilterBar,
  Icon,
  LinearProgress,
  LoadingState,
  Page,
  PageHeader,
  Select,
  StatCard,
  StatusPill,
  Tabs,
  TextField,
} from '../components/ui'

/**
 * F-060 — report lead generation: filtri, funnel a barre (stesso calcolo di `/funnels`), KPI cliccabili con
 * drill-down ai record, coorti 7/30/60 giorni e contestazioni (F-120: con l'esito, il riepilogo per fornitore e
 * l'export per il fornitore). Nessun importo. Non esiste l'"opportunità": non è mostrata né simulata. Il filtro
 * Regione usa la provincia del lead (F-131); il "portale" è il Fornitore.
 */

const formatNum = (v: number): string => v.toLocaleString('it-IT')

/** Tassi 0..1 dal backend; null = denominatore 0 oppure finestra non ancora misurabile (mai "0%"). */
function formatPct(v: number | null): string {
  if (v === null) return '—'
  return (v * 100).toLocaleString('it-IT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + '%'
}

/** F-131: le venti regioni (come nel backend `ItalianProvinces`) per il filtro Regione. */
const REGIONS: string[] = [
  'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna', 'Friuli-Venezia Giulia', 'Lazio', 'Liguria',
  'Lombardia', 'Marche', 'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana', 'Trentino-Alto Adige',
  'Umbria', "Valle d'Aosta", 'Veneto',
]

const KPI_LABELS: Record<DrilldownKpi, string> = {
  received: 'Lead ricevuti',
  valid: 'Lead validi',
  duplicates: 'Duplicati',
  invalidNumbers: 'Numeri non validi',
  notQualified: 'Non qualificati (persi)',
  contested: 'Contestati',
  contacted: 'Contattati',
  qualified: 'Qualificati',
  noShow: 'No show',
  appointmentsSet: 'Appuntamenti fissati',
  appointmentsCompleted: 'Appuntamenti effettuati',
  contracts: 'Contratti',
  activations: 'Attivazioni',
}

type Tab = 'funnel' | 'cohorts' | 'disputes' | 'providers'

function FunnelBar({
  label,
  count,
  maxCount,
  rate,
  onClick,
}: {
  label: string
  count: number
  maxCount: number
  rate: { value: number | null; label: string } | null
  onClick: () => void
}) {
  const widthPct = maxCount === 0 ? 0 : Math.max(2, (count / maxCount) * 100)
  return (
    <div className="mb-2">
      {rate && (
        <div className="d-flex align-items-center gap-2 mb-1 ps-1">
          <Icon name="arrow_downward" size={18} className="text-muted" />
          <span className="text-muted md-typescale-body-small">
            {rate.label}: <strong>{formatPct(rate.value)}</strong>
          </span>
        </div>
      )}
      <div className="d-flex align-items-center gap-3">
        <LinearProgress className="flex-grow-1" value={widthPct} aria-label={label} />
        <Button variant="text" className="md-u-minw-220 md-u-justify-start" onClick={onClick}>
          <span className="text-start">
            <span className="md-typescale-emphasized-body-medium d-block">{label}</span>
            <span className="d-block text-muted md-typescale-body-small md-num">{formatNum(count)}</span>
          </span>
        </Button>
      </div>
    </div>
  )
}

function KpiCard({ kpi, value, onOpen }: { kpi: DrilldownKpi; value: number; onOpen: (k: DrilldownKpi) => void }) {
  return <StatCard label={KPI_LABELS[kpi]} value={formatNum(value)} onClick={() => onOpen(kpi)} title="Vedi i record" />
}

function WindowCell({ w }: { w: CohortWindow }) {
  if (w.count === null) return <td className="text-muted text-end" title="Coorte più giovane della finestra">—</td>
  return (
    <td className="text-end md-num">
      {formatNum(w.count)} <span className="text-muted md-typescale-body-small">({formatPct(w.rate)})</span>
    </td>
  )
}

function DisputeTable({ rows, reasons }: { rows: DisputeBucket[]; reasons: string[] }) {
  return (
    <DataTable>
        <thead>
          <tr>
            <th>Gruppo</th>
            <th className="text-end">Ricevuti</th>
            <th className="text-end">Contestati</th>
            <th className="text-end">% contestati</th>
            <th className="text-end">In attesa</th>
            <th className="text-end">Accettati</th>
            <th className="text-end">Respinti</th>
            <th className="text-end">% accettati</th>
            {reasons.map(r => (
              <th key={r} className="text-end md-typescale-body-small">
                {r}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.key}>
              <td>{row.label}</td>
              <td className="text-end md-num">{formatNum(row.received)}</td>
              <td className="text-end md-num">{formatNum(row.contested)}</td>
              <td className="text-end md-num">{formatPct(row.contestRate)}</td>
              <td className="text-end md-num">{formatNum(row.pending)}</td>
              <td className="text-end md-num">{formatNum(row.accepted)}</td>
              <td className="text-end md-num">{formatNum(row.rejected)}</td>
              <td className="text-end md-num">{formatPct(row.acceptanceRate)}</td>
              {reasons.map(r => (
                <td key={r} className="text-end">
                  {formatNum(row.byReason.find(x => x.reason === r)?.count ?? 0)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </DataTable>
  )
}

const formatEur = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' })

/** R3-11: cruscotto LG (tassi del call center, CAC) e colonne economiche per fornitore; le colonne importi esistono solo se arrivano. */
function CompleteExtras({ data }: { data: LeadProvidersComplete }) {
  const cc = data.dashboard.callCenter
  const econ = new Map((data.economics ?? []).map(e => [e.key, e]))
  const hasMoney = data.economics !== undefined
  return (
    <div className="p-3">
      <StatGrid>
        <StatCard label="Contatto" value={formatPct(cc.contactRate)} />
        <StatCard label="Qualificazione" value={formatPct(cc.qualificationRate)} />
        <StatCard label="Appuntamento" value={formatPct(cc.appointmentRate)} />
        <StatCard label="Presenza" value={formatPct(cc.showRate)} />
        <StatCard label="Chiusura" value={formatPct(cc.closingRate)} />
        <StatCard label="Lead → attivazione" value={formatPct(cc.leadToActivation)} />
        {hasMoney && <StatCard label="CAC" value={formatEur(data.dashboard.cac)} title="Spesa / pratiche attivate" />}
      </StatGrid>
      <DataTable variant="plain">
        <thead>
          <tr>
            <th>Fornitore</th>
            <th className="text-end">Attivati</th>
            <th className="text-end">% chiusura</th>
            <th className="text-end">% attivazione</th>
            {hasMoney && (
              <>
                <th className="text-end">Spesa</th>
                <th className="text-end">Ricavo maturato</th>
                <th className="text-end">Margine</th>
                <th className="text-end">ROI</th>
                <th className="text-end">CAC</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {data.items.map(r => {
            const e = econ.get(r.key)
            return (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td className="text-end md-num">{formatNum(r.activated ?? 0)}</td>
                <td className="text-end md-num">{formatPct(r.rates.closingRate ?? null)}</td>
                <td className="text-end md-num">{formatPct(r.rates.leadToActivation ?? null)}</td>
                {hasMoney && (
                  <>
                    <td className="text-end md-num">{formatEur(e?.spend ?? 0)}</td>
                    <td className="text-end md-num">{formatEur(e?.accrued ?? 0)}</td>
                    <td className="text-end md-num">{formatEur(e?.margin ?? 0)}</td>
                    <td className="text-end md-num">{e?.roi == null ? '—' : formatPct(e.roi)}</td>
                    <td className="text-end md-num">{formatEur(e?.cac)}</td>
                  </>
                )}
              </tr>
            )
          })}
        </tbody>
      </DataTable>
      <ul className="md-typescale-body-small text-muted mb-0 ps-3 mt-2">
        {data.limitations.map(l => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </div>
  )
}

function ProvidersTable({ data }: { data: LeadProviders }) {
  return (
    <DataTable>
        <thead>
          <tr>
            <th>Fornitore</th>
            <th className="text-end">Ricevuti</th>
            <th className="text-end">Validi</th>
            <th className="text-end">Duplicati</th>
            <th className="text-end">Num. non validi</th>
            <th className="text-end">Contestati</th>
            <th className="text-end">Accettati</th>
            <th className="text-end">Contattati</th>
            <th className="text-end">Qualificati</th>
            <th className="text-end">Appuntamenti</th>
            <th className="text-end">Convertiti</th>
            <th className="text-end">% validi</th>
            <th className="text-end">% duplicati</th>
            <th className="text-end">% contestati</th>
            <th className="text-end">% conversione</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map(r => (
            <tr key={r.key}>
              <td>{r.label}</td>
              <td className="text-end md-num">{formatNum(r.received)}</td>
              <td className="text-end md-num">{formatNum(r.valid)}</td>
              <td className="text-end md-num">{formatNum(r.duplicates)}</td>
              <td className="text-end md-num">{formatNum(r.invalidNumbers)}</td>
              <td className="text-end md-num">{formatNum(r.contested)}</td>
              <td className="text-end md-num">{formatNum(r.accepted)}</td>
              <td className="text-end md-num">{formatNum(r.contacted)}</td>
              <td className="text-end md-num">{formatNum(r.qualified)}</td>
              <td className="text-end md-num">{formatNum(r.appointmentsSet)}</td>
              <td className="text-end md-num">{formatNum(r.converted)}</td>
              <td className="text-end md-num">{formatPct(r.rates.validRate)}</td>
              <td className="text-end md-num">{formatPct(r.rates.duplicateRate)}</td>
              <td className="text-end md-num">{formatPct(r.rates.contestRate)}</td>
              <td className="text-end md-num">{formatPct(r.rates.leadToSale)}</td>
            </tr>
          ))}
          {data.items.length === 0 && (
            <DataTableEmptyRow colSpan={15} title="Nessun lead nel periodo." />
          )}
        </tbody>
      </DataTable>
  )
}

export function LeadGenerationReportPage() {
  const [form, setForm] = useState<LeadGenerationFilters>({})
  const [applied, setApplied] = useState<LeadGenerationFilters>({})
  const [tab, setTab] = useState<Tab>('funnel')
  // R5-03: le fonti vengono dall'elenco dell'Admin (anche le disattivate, perche' il report legge i lead storici).
  const [sources, setSources] = useState<LeadSourceOption[]>([])
  useEffect(() => {
    let alive = true
    leadsApi.sources(true).then(r => { if (alive) setSources(r) }).catch(() => { if (alive) setSources([]) })
    return () => { alive = false }
  }, [])

  const [report, setReport] = useState<LeadGenerationReport | null>(null)
  const [cohorts, setCohorts] = useState<LeadCohorts | null>(null)
  const [granularity, setGranularity] = useState<'week' | 'month'>('week')
  const [disputes, setDisputes] = useState<LeadDisputes | null>(null)
  const [groupBy, setGroupBy] = useState<DisputeGroupBy>('source')
  const [providers, setProviders] = useState<LeadProviders | null>(null)
  const [exporting, setExporting] = useState(false)
  const excelOn = useFeatureFlag('export.excel')
  const completeOn = useFeatureFlag('report.lead_completo')
  const [complete, setComplete] = useState<LeadProvidersComplete | null>(null)

  const [drillKpi, setDrillKpi] = useState<DrilldownKpi | null>(null)
  const [drill, setDrill] = useState<Drilldown | null>(null)
  const [page, setPage] = useState(1)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const loadReport = useCallback(async (f: LeadGenerationFilters) => {
    setLoading(true)
    setError('')
    try {
      setReport(await leadGenerationReportApi.report(f))
    } catch (e) {
      setReport(null)
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadReport(applied)
    setDrillKpi(null)
    setDrill(null)
  }, [applied, loadReport])

  useEffect(() => {
    if (tab !== 'cohorts') return
    setError('')
    leadGenerationReportApi
      .cohorts(applied, granularity)
      .then(setCohorts)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore'))
  }, [tab, applied, granularity])

  useEffect(() => {
    if (tab !== 'disputes') return
    setError('')
    leadGenerationReportApi
      .disputes(applied, groupBy)
      .then(setDisputes)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore'))
  }, [tab, applied, groupBy])

  useEffect(() => {
    if (tab !== 'providers') return
    setError('')
    leadGenerationReportApi
      .providers(applied)
      .then(setProviders)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore'))
  }, [tab, applied])

  useEffect(() => {
    if (tab !== 'providers' || !completeOn) return
    leadGenerationReportApi
      .providersComplete(applied)
      .then(setComplete)
      .catch(() => setComplete(null))
  }, [tab, applied, completeOn])

  async function exportForProvider(providerId?: string | number, format: 'csv' | 'xlsx' = 'csv') {
    setExporting(true)
    setError('')
    try {
      saveExportFile(
        await downloadExportFile(
          withFormat(leadGenerationReportApi.disputesExportUrl(providerId, applied.from, applied.to), format),
        ),
      )
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setExporting(false)
    }
  }

  useEffect(() => {
    if (!drillKpi) return
    leadGenerationReportApi
      .drilldown(applied, drillKpi, page)
      .then(setDrill)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore'))
  }, [drillKpi, applied, page])

  function openDrill(kpi: DrilldownKpi) {
    setDrill(null)
    setPage(1)
    setDrillKpi(kpi)
  }

  function setField(key: keyof LeadGenerationFilters, value: string) {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const field = (key: keyof LeadGenerationFilters, label: string, type = 'text') => (
    <div key={key}>
      <TextField
        label={label}
        type={type}
        value={form[key] ?? ''}
        onChange={e => setField(key, e.target.value)}
      />
    </div>
  )

  const st = report?.stages
  const rt = report?.conversionRates
  const maxCount = st?.totalLeads ?? 0
  const reasonColumns = disputes?.total.byReason.map(r => r.reason) ?? []

  return (
    <Page>
      <PageHeader title="Lead generation" icon="leaderboard" actions={<ReportExportButtons title="Report Lead Generation" />} />

      <FilterBar
        onSubmit={() => setApplied({ ...form })}
        onReset={() => {
          setForm({})
          setApplied({})
        }}
      >
        {field('from', 'Da (creazione lead)', 'date')}
        {field('to', 'A', 'date')}
        <Select label="Fonte" value={form.source ?? ''} onChange={e => setField('source', e.target.value)}>
          <option value="">Tutte</option>
          {sources.map(s => (
            <option key={s.code} value={s.code}>
              {s.label}{s.isActive === false ? ' (disattivata)' : ''}
            </option>
          ))}
        </Select>
        {field('campaignId', 'Campagna (id)', 'number')}
        {field('leadProviderId', 'Fornitore (id)', 'number')}
        {field('callCenterSource', 'Origine call center')}
        {field('agentId', 'Agente (id)', 'number')}
        {field('teamOf', 'Team di (id utente)', 'number')}
        {field('sector', 'Settore di interesse')}
        {field('departmentId', 'Reparto (id)', 'number')}
        {field('operatorId', 'Operatore (id)', 'number')}
        {field('productId', 'Prodotto (id)', 'number')}
        <Select label="Regione (dalla provincia del lead)" value={form.region ?? ''} onChange={e => setField('region', e.target.value)}>
          <option value="">Tutte</option>
          {REGIONS.map(r => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </Select>
      </FilterBar>
      <div className="text-muted md-typescale-body-small">
        Operatore e prodotto limitano il report ai lead già convertiti in un contratto con quell&apos;operatore/prodotto. Il
        fornitore si raggiunge solo tramite le sue campagne.
      </div>

      {error && <Callout tone="error">Non è stato possibile caricare il report. {error}</Callout>}

      <Tabs
        aria-label="Sezioni del report"
        value={tab}
        onChange={id => setTab(id as Tab)}
        items={[
          { id: 'funnel', label: 'Funnel e KPI' },
          { id: 'cohorts', label: 'Coorti' },
          { id: 'disputes', label: 'Contestazioni' },
          { id: 'providers', label: 'Fornitori' },
        ]}
      />

      {tab === 'funnel' && (
        <>
          {loading && <LoadingState />}

          {report && st && rt && (
            <>
              {(() => {
                const badge = dataSourceBadge((report as LeadGenerationReport & WithDataSource).dataSource,
                  (report as LeadGenerationReport & WithDataSource).computedAt)
                return badge && (
                  <div>
                    <StatusPill
                      tone={badge.aggregate ? 'info' : 'neutral'}
                      title={badge.title}
                      data-testid="data-source-badge"
                    >
                      Fonte dati: {badge.text}
                    </StatusPill>
                  </div>
                )
              })()}
              <Card>
                <CardContent>
                  <FunnelBar label="Lead totali" count={st.totalLeads} maxCount={maxCount} rate={null} onClick={() => openDrill('received')} />
                  <FunnelBar
                    label="Contattati"
                    count={st.contacted}
                    maxCount={maxCount}
                    rate={{ value: rt.contactRate, label: 'Contact Rate (contattati / lead totali)' }}
                    onClick={() => openDrill('contacted')}
                  />
                  <FunnelBar
                    label="Qualificati"
                    count={st.qualified}
                    maxCount={maxCount}
                    rate={{ value: rt.qualificationRate, label: 'Qualification Rate (qualificati / contattati)' }}
                    onClick={() => openDrill('qualified')}
                  />
                  <FunnelBar
                    label="Appuntamenti fissati"
                    count={st.appointmentsSet}
                    maxCount={maxCount}
                    rate={{ value: rt.appointmentRate, label: 'Appointment Rate (fissati / contattati)' }}
                    onClick={() => openDrill('appointmentsSet')}
                  />
                  <FunnelBar
                    label="Appuntamenti effettuati"
                    count={st.appointmentsCompleted}
                    maxCount={maxCount}
                    rate={{ value: rt.showRate, label: 'Show Rate (effettuati / fissati)' }}
                    onClick={() => openDrill('appointmentsCompleted')}
                  />
                  <FunnelBar
                    label="Contratti"
                    count={st.contracts}
                    maxCount={maxCount}
                    rate={{ value: rt.closingRate, label: 'Closing Rate (contratti / effettuati)' }}
                    onClick={() => openDrill('contracts')}
                  />
                  <FunnelBar
                    label="Attivazioni"
                    count={st.activations}
                    maxCount={maxCount}
                    rate={{ value: rt.activationRateOnContracts, label: 'Activation Rate su contratti (attivazioni / contratti)' }}
                    onClick={() => openDrill('activations')}
                  />
                </CardContent>
              </Card>

              <StatGrid>
                <KpiCard kpi="received" value={report.kpi.received} onOpen={openDrill} />
                <KpiCard kpi="valid" value={report.kpi.valid} onOpen={openDrill} />
                <KpiCard kpi="duplicates" value={report.kpi.duplicates} onOpen={openDrill} />
                <KpiCard kpi="notQualified" value={report.kpi.notQualified} onOpen={openDrill} />
                <KpiCard kpi="noShow" value={report.kpi.noShow} onOpen={openDrill} />
                <KpiCard kpi="contested" value={report.kpi.contested} onOpen={openDrill} />
              </StatGrid>

              {drillKpi && (
                <Card padding="none">
                  <CardHeader
                    title={`${KPI_LABELS[drillKpi]}${drill ? ` — ${formatNum(drill.total)} record` : ''}`}
                    actions={<Button size="sm" variant="text" onClick={() => setDrillKpi(null)}>Chiudi</Button>}
                  />
                  <div>
                    {!drill ? (
                      <div className="p-3 text-muted">Caricamento…</div>
                    ) : drill.items.length === 0 ? (
                      <div className="p-3 text-muted">Nessun record.</div>
                    ) : (
                      <DataTable variant="plain">
                          {drill.entity === 'lead' && (
                            <>
                              <thead>
                                <tr>
                                  <th>Lead</th>
                                  <th>Fonte</th>
                                  <th>Stato</th>
                                  <th>Creato il</th>
                                  <th>Contestazione</th>
                                </tr>
                              </thead>
                              <tbody>
                                {drill.items.map(i => (
                                  <tr key={i.id}>
                                    <td>
                                      <Link to={`/leads/${i.id}`}>{i.name}</Link>
                                    </td>
                                    <td>{i.source ?? '—'}</td>
                                    <td>{i.status}</td>
                                    <td>{new Date(i.createdAt).toLocaleDateString('it-IT')}</td>
                                    <td>{i.isContested ? (i.contestReason ?? 'Sì') : '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </>
                          )}
                          {drill.entity === 'appointment' && (
                            <>
                              <thead>
                                <tr>
                                  <th>Appuntamento</th>
                                  <th>Lead</th>
                                  <th>Stato</th>
                                  <th>Data</th>
                                </tr>
                              </thead>
                              <tbody>
                                {drill.items.map(i => (
                                  <tr key={i.id}>
                                    <td>{i.title}</td>
                                    <td>{i.leadId ? <Link to={`/leads/${i.leadId}`}>Lead #{i.leadId}</Link> : '—'}</td>
                                    <td>{i.status}</td>
                                    <td>{new Date(i.startAt).toLocaleString('it-IT')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </>
                          )}
                          {drill.entity === 'contract' && (
                            <>
                              <thead>
                                <tr>
                                  <th>Pratica</th>
                                  <th>Lead</th>
                                  <th>Stato</th>
                                </tr>
                              </thead>
                              <tbody>
                                {drill.items.map(i => (
                                  <tr key={`${i.leadId}-${i.contractId}`}>
                                    <td>
                                      {i.contractVisible === false ? (
                                        <span className="text-muted" title="Pratica non visibile con i tuoi permessi">
                                          Pratica #{i.contractId}
                                        </span>
                                      ) : (
                                        <Link to={`/contracts/${i.contractId}`}>{i.referenceCode ?? `Pratica #${i.contractId}`}</Link>
                                      )}
                                    </td>
                                    <td>
                                      <Link to={`/leads/${i.leadId}`}>{i.leadName ?? `Lead #${i.leadId}`}</Link>
                                    </td>
                                    <td>{i.status ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </>
                          )}
                        </DataTable>
                    )}
                  </div>
                  {drill && drill.total > drill.pageSize && (
                    <div className="d-flex gap-2 align-items-center p-2">
                      <Button size="sm" variant="outlined" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                        Precedente
                      </Button>
                      <span className="md-typescale-body-small text-muted">
                        Pagina {drill.page} di {Math.ceil(drill.total / drill.pageSize)}
                      </span>
                      <Button
                        size="sm"
                        variant="outlined"
                        disabled={page >= Math.ceil(drill.total / drill.pageSize)}
                        onClick={() => setPage(p => p + 1)}
                      >
                        Successiva
                      </Button>
                    </div>
                  )}
                </Card>
              )}

              <CardGrid min={360}>
                  <Card padding="none">
                    <CardHeader title="Per fonte" />
                    <DataTable variant="plain">
                        <thead>
                          <tr>
                            <th>Fonte</th>
                            <th className="text-end">Ricevuti</th>
                            <th className="text-end">Validi</th>
                            <th className="text-end">Duplicati</th>
                            <th className="text-end">Persi</th>
                            <th className="text-end">Contestati</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.bySource.map(r => (
                            <tr key={r.source}>
                              <td>{r.label}</td>
                              <td className="text-end md-num">{formatNum(r.received)}</td>
                              <td className="text-end md-num">{formatNum(r.valid)}</td>
                              <td className="text-end md-num">{formatNum(r.duplicates)}</td>
                              <td className="text-end md-num">{formatNum(r.notQualified)}</td>
                              <td className="text-end md-num">{formatNum(r.contested)}</td>
                            </tr>
                          ))}
                          {report.bySource.length === 0 && (
                            <DataTableEmptyRow colSpan={6} title="Nessun lead nel perimetro." />
                          )}
                        </tbody>
                      </DataTable>
                  </Card>
                  <Card padding="none">
                    <CardHeader title="Per campagna" />
                    <DataTable variant="plain">
                        <thead>
                          <tr>
                            <th>Campagna</th>
                            <th className="text-end">Ricevuti</th>
                            <th className="text-end">Validi</th>
                            <th className="text-end">Duplicati</th>
                            <th className="text-end">Persi</th>
                            <th className="text-end">Contestati</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.byCampaign.map(r => (
                            <tr key={r.campaignId ?? 'none'}>
                              <td>{r.name}</td>
                              <td className="text-end md-num">{formatNum(r.received)}</td>
                              <td className="text-end md-num">{formatNum(r.valid)}</td>
                              <td className="text-end md-num">{formatNum(r.duplicates)}</td>
                              <td className="text-end md-num">{formatNum(r.notQualified)}</td>
                              <td className="text-end md-num">{formatNum(r.contested)}</td>
                            </tr>
                          ))}
                          {report.byCampaign.length === 0 && (
                            <DataTableEmptyRow colSpan={6} title="Nessun lead nel perimetro." />
                          )}
                        </tbody>
                      </DataTable>
                  </Card>
              </CardGrid>

              <Callout tone="info" className="md-typescale-body-small">
                <ul className="mb-0 ps-3">
                  {report.limitations.map(l => (
                    <li key={l}>{l}</li>
                  ))}
                </ul>
              </Callout>
            </>
          )}
        </>
      )}

      {tab === 'cohorts' && (
        <Card padding="none">
          <CardHeader
            title="Coorti per data di creazione del lead"
            actions={
              <Select
                label="Granularità"
                value={granularity}
                onChange={e => setGranularity(e.target.value as 'week' | 'month')}
              >
                <option value="week">Settimana</option>
                <option value="month">Mese</option>
              </Select>
            }
          />
          <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Coorte</th>
                  <th className="text-end">Lead</th>
                  <th className="text-end">Convertiti 7 gg</th>
                  <th className="text-end">30 gg</th>
                  <th className="text-end">60 gg</th>
                  <th className="text-end">Attivati 7 gg</th>
                  <th className="text-end">30 gg</th>
                  <th className="text-end">60 gg</th>
                </tr>
              </thead>
              <tbody>
                {cohorts?.cohorts.map(c => (
                  <tr key={c.start}>
                    <td>
                      {new Date(c.start).toLocaleDateString('it-IT')} – {new Date(c.end).toLocaleDateString('it-IT')}
                    </td>
                    <td className="text-end md-num">{formatNum(c.totalLeads)}</td>
                    <WindowCell w={c.converted.d7} />
                    <WindowCell w={c.converted.d30} />
                    <WindowCell w={c.converted.d60} />
                    <WindowCell w={c.activated.d7} />
                    <WindowCell w={c.activated.d30} />
                    <WindowCell w={c.activated.d60} />
                  </tr>
                ))}
                {cohorts && cohorts.cohorts.length === 0 && (
                  <DataTableEmptyRow colSpan={8} title="Nessun lead nel perimetro." />
                )}
              </tbody>
            </DataTable>
          {cohorts && <div className="md-typescale-body-small text-muted p-3">{cohorts.note} Un trattino indica una finestra non ancora misurabile.</div>}
        </Card>
      )}

      {tab === 'disputes' && (
        <Card padding="none">
          <CardHeader
            title="Contestazioni"
            actions={
              <>
                <Button
                  size="sm"
                  variant="outlined"
                  disabled={exporting}
                  title="CSV senza dati personali, per il periodo e il fornitore (filtro) selezionati"
                  onClick={() => void exportForProvider(applied.leadProviderId)}
                >
                  {exporting ? 'Esporto…' : 'Esporta per il fornitore'}
                </Button>
                {excelOn && (
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={exporting}
                    title="Excel senza dati personali, per il periodo e il fornitore (filtro) selezionati"
                    onClick={() => void exportForProvider(applied.leadProviderId, 'xlsx')}
                  >
                    Excel
                  </Button>
                )}
                <Select label="Raggruppa" value={groupBy} onChange={e => setGroupBy(e.target.value as DisputeGroupBy)}>
                  <option value="source">Per fonte</option>
                  <option value="campaign">Per campagna</option>
                  <option value="provider">Per fornitore</option>
                </Select>
              </>
            }
          />
          {disputes && <DisputeTable rows={[disputes.total, ...disputes.items]} reasons={reasonColumns} />}
          {disputes && (
            <div className="md-typescale-body-small text-muted p-3">
              <ul className="mb-0 ps-3">
                {disputes.limitations.map(l => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}

      {tab === 'providers' && (
        <Card padding="none">
          <CardHeader title="Fornitori" subtitle={completeOn ? 'Qualità dei lead per fornitore, cruscotto del call center e, per chi li vede, spesa e ricavo.' : 'Qualità dei lead per fornitore: nessun importo.'} />
          {providers && <ProvidersTable data={providers} />}
          {completeOn && complete && <CompleteExtras data={complete} />}
          {providers && (
            <div className="md-typescale-body-small text-muted p-3">
              <ul className="mb-0 ps-3">
                {providers.limitations.map(l => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </div>
          )}
        </Card>
      )}
    </Page>
  )
}
